import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportReportWithPlantHeader } from '../../utils/reportExcelExport';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { buildCustomerLedgerPdfHeader } from '../../utils/customerLedgerPdf';
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { Button } from 'reactstrap';
import Select from 'react-select';
import { DatePicker } from 'antd';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCompanies } from '../../services/CompanyServices';
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import { getMarketingPersonReport } from '../../services/ReportServices';
import { displayError } from '../../helpers';
import { HandleSelect, reportMultiSelectComponents, reportFilterSelectStyles } from '../../components/MultiSelectControls';
import ReportExportDropdown from '../../components/ReportExportDropdown';
import { useReportTableExports } from '../../hooks/useReportTableExports';

const { RangePicker } = DatePicker;
const filterSelectStyles = reportFilterSelectStyles;

const REPORT_COLUMNS = [
    { fieldName: 'marketing_name', headerName: 'Marketing Person', size: 140 },
    { fieldName: 'total_customers', headerName: 'Customers', size: 88, align: 'right' },
    { fieldName: 'new_customers', headerName: 'New Customers', size: 100, align: 'right' },
    { fieldName: 'quotation_count', headerName: 'Quotations', size: 90, align: 'right' },
    { fieldName: 'quotation_amount', headerName: 'Quote Amount', size: 105, align: 'right' },
    { fieldName: 'sales_order_count', headerName: 'Sales Orders', size: 95, align: 'right' },
    { fieldName: 'sales_order_amount', headerName: 'SO Amount', size: 100, align: 'right' },
    { fieldName: 'sales_count', headerName: 'Invoices', size: 80, align: 'right' },
    { fieldName: 'sales_amount', headerName: 'Sales Amount', size: 105, align: 'right' },
    { fieldName: 'collection_amount', headerName: 'Collections', size: 105, align: 'right' },
    { fieldName: 'balance_amount', headerName: 'Balance', size: 100, align: 'right' },
    { fieldName: 'conversion_pct', headerName: 'Conversion %', size: 95, align: 'right' },
    { fieldName: 'collection_pct', headerName: 'Collection %', size: 95, align: 'right' },
];

function resolveMultiIds(selected, allOptions) {
    if (!selected || selected.length === 0) {
        return '*';
    }
    if (selected.some((item) => item.value === 'select-all')) {
        return allOptions.map((item) => item.value).join(',') || '*';
    }
    return selected.map((item) => item.value).join(',');
}

function formatAmount(value) {
    return Number(value || 0).toFixed(2);
}

function MarketingPersonReport() {
    const [cookies] = useCookies(['myToken']);
    const [records, setRecords] = useState([]);
    const [totals, setTotals] = useState(null);
    const [plants, setPlants] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [salesreps, setSalesreps] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [selectedSalesrep, setSelectedSalesrep] = useState([]);
    const [fromDate, setFromDate] = useState(moment().startOf('month'));
    const [toDate, setToDate] = useState(moment());
    const [asOnDate, setAsOnDate] = useState(moment());
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [hasGenerated, setHasGenerated] = useState(false);

    const companiesWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All companies' }, ...companies],
        [companies],
    );
    const plantsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All plants' }, ...plants],
        [plants],
    );
    const salesrepsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All sales reps' }, ...salesreps],
        [salesreps],
    );

    useEffect(() => {
        getAllCompanies(cookies).then((companyList) => {
            setCompanies((companyList.company_data || [])
                .filter((company) => company.status === true || company.status === 1 || company.status === 'Active')
                .map((company) => ({
                    value: company.id,
                    label: company.alias || company.entity_name,
                })));
        });
        getUserAllowedPlants(cookies).then((plantList) => {
            setPlants((plantList.plant_list || [])
                .filter((plant) => plant.status)
                .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
        });
        getAllSalesRepresentatives(cookies).then((salesRepList) => {
            setSalesreps((salesRepList.salesrep_data || [])
                .filter((salesrep) => salesrep.status === true || salesrep.status === 1 || salesrep.status === 'Active')
                .map((salesrep) => ({ value: salesrep.id, label: salesrep.entity_name })));
        });
    }, [cookies]);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return records;
        }
        return records.filter((row) => REPORT_COLUMNS.some((col) => {
            const value = row[col.fieldName];
            return value !== null && value !== undefined && String(value).toLowerCase().includes(query);
        }));
    }, [records, searchText]);

    const exportFileBase = useMemo(() => {
        const datePart = fromDate?.isValid() && toDate?.isValid()
            ? `${fromDate.format('DD-MM-YYYY')}_to_${toDate.format('DD-MM-YYYY')}`
            : moment().format('DD-MM-YYYY');
        return `MarketingPersonReport_${datePart}`;
    }, [fromDate, toDate]);

    const pdfSummaryLines = useMemo(() => {
        if (!fromDate?.isValid() || !toDate?.isValid()) {
            return [];
        }
        const lines = [`Period: ${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')}`];
        if (asOnDate?.isValid()) {
            lines.push(`Balance as on: ${asOnDate.format('DD-MM-YYYY')}`);
        }
        return lines;
    }, [asOnDate, fromDate, toDate]);

    const summaryCards = useMemo(() => {
        if (!totals) {
            return [];
        }
        return [
            { label: 'Customers', value: String(totals.total_customers || 0) },
            { label: 'New Customers', value: String(totals.new_customers || 0) },
            { label: 'Quotations', value: String(totals.quotation_count || 0) },
            { label: 'Sales Amount', value: formatAmount(totals.sales_amount) },
            { label: 'Collections', value: formatAmount(totals.collection_amount) },
            { label: 'Balance', value: formatAmount(totals.balance_amount) },
        ];
    }, [totals]);

    const loadReport = useCallback(() => {
        if (!fromDate?.isValid() || !toDate?.isValid()) {
            displayError({ message: 'Please select a valid date range.' }, 'Report Failed');
            return Promise.resolve();
        }
        if (!asOnDate?.isValid()) {
            displayError({ message: 'Please select a valid balance as-on date.' }, 'Report Failed');
            return Promise.resolve();
        }

        setIsLoading(true);
        const params = {
            company_id: resolveMultiIds(selectedCompany, companies),
            plant_id: resolveMultiIds(selectedPlant, plants),
            salesrep_ids: resolveMultiIds(selectedSalesrep, salesreps),
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
            as_on_date: asOnDate.format('DD-MM-YYYY'),
        };

        return getMarketingPersonReport(cookies, params)
            .then((response) => {
                setRecords(response.marketing_person_report_list || []);
                setTotals(response.totals || null);
                setHasGenerated(true);
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Report Failed');
                setRecords([]);
                setTotals(null);
            })
            .finally(() => setIsLoading(false));
    }, [asOnDate, cookies, companies, fromDate, plants, salesreps, selectedCompany, selectedPlant, selectedSalesrep, toDate]);

    const ensureExportData = () => {
        if (!hasGenerated || !filteredRecords.length) {
            displayError({ message: 'Generate the report first, then export.' }, 'Export Failed');
            return false;
        }
        return true;
    };

    const handleCSVExport = () => {
        if (!ensureExportData()) return;
        exportAsCSV(REPORT_COLUMNS, filteredRecords, exportFileBase);
    };

    const handleExcelExport = async () => {
        if (!ensureExportData()) return;
        setIsLoading(true);
        try {
            const plantIds = (selectedPlant || [])
                .filter((plant) => plant?.value && plant.value !== 'select-all')
                .map((plant) => plant.value);
            await exportReportWithPlantHeader({
                cookies,
                plantIds,
                columns: REPORT_COLUMNS,
                records: filteredRecords,
                filename: exportFileBase,
                documentTitle: 'Marketing Person Report',
                infoLines: pdfSummaryLines,
            });
        } catch (error) {
            displayError({ message: error?.message || 'Excel export failed.' }, 'Export Failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePDFExport = async () => {
        if (!ensureExportData()) return;
        setIsLoading(true);
        try {
            const header = await buildCustomerLedgerPdfHeader(cookies, [], [], {
                documentTitle: 'Marketing Person Report',
                subtitle: pdfSummaryLines.join(' | '),
            });
            const pdfColumns = REPORT_COLUMNS.map((col) => ({
                header: col.headerName,
                dataKey: col.fieldName,
                align: col.align,
            }));
            const blob = createPdfBlob('', pdfColumns, filteredRecords, pdfSummaryLines, header);
            openPdfInApp(blob, {
                title: 'Marketing Person Report',
                downloadFileName: `${exportFileBase}.pdf`,
            });
        } catch (error) {
            displayError({ message: error?.message || 'PDF export failed.' }, 'Export Failed');
        } finally {
            setIsLoading(false);
        }
    };

    const { onImage: handleImageExport } = useReportTableExports({
        columns: REPORT_COLUMNS,
        records: filteredRecords,
        title: 'Marketing Person Report',
        filename: exportFileBase,
    });

    const exportButtons = (
        <ReportExportDropdown
            onCSV={handleCSVExport}
            onExcel={handleExcelExport}
            onPDF={handlePDFExport}
            onImage={handleImageExport}
            disabled={isLoading || !filteredRecords.length}
        />
    );

    const toolbarFilters = (
        <>
            <div className="list-page-toolbar-filters-row">
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            styles={filterSelectStyles}
                            options={companiesWithSelectAll}
                            value={selectedCompany}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedCompany,
                                setSelectedCompany,
                                companies,
                                companiesWithSelectAll,
                            )}
                            placeholder="Company"
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            styles={filterSelectStyles}
                            options={plantsWithSelectAll}
                            value={selectedPlant}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedPlant,
                                setSelectedPlant,
                                plants,
                                plantsWithSelectAll,
                            )}
                            placeholder="Plant"
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            styles={filterSelectStyles}
                            options={salesrepsWithSelectAll}
                            value={selectedSalesrep}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedSalesrep,
                                setSelectedSalesrep,
                                salesreps,
                                salesrepsWithSelectAll,
                            )}
                            placeholder="Marketing Person"
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter-date">
                        <RangePicker
                            value={[fromDate, toDate]}
                            format="DD-MM-YYYY"
                            onChange={(values) => {
                                if (values?.[0] && values?.[1]) {
                                    setFromDate(values[0]);
                                    setToDate(values[1]);
                                    if (!asOnDate || values[1].isAfter(asOnDate)) {
                                        setAsOnDate(values[1]);
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter-date">
                        <DatePicker
                            value={asOnDate}
                            format="DD-MM-YYYY"
                            placeholder="Balance as on"
                            onChange={(value) => {
                                if (value) {
                                    setAsOnDate(value);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="list-page-toolbar-filters-row list-page-toolbar-filters-row-actions">
                {exportButtons}
                <Button
                    color="primary"
                    className="list-page-toolbar-generate-btn"
                    onClick={loadReport}
                    disabled={isLoading}
                >
                    Generate
                </Button>
            </div>
        </>
    );

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap" className="marketing-person-report-page">
                <div className="marketing-person-report-shell">
                    <ListPageLayout
                        moduleLabel="REPORTS"
                        title="Marketing Person Report"
                        subtitle="Analyse each marketing person by company, customers, quotations, sales, collections, balance, and performance for the selected period."
                        modeLabel="REPORT"
                        recordCount={filteredRecords.length}
                        recordLabel="marketing persons"
                        searchText={searchText}
                        onSearchChange={setSearchText}
                        searchPlaceholder="Search marketing person..."
                        toolbarFilters={toolbarFilters}
                        toolbarVariant="stacked"
                        reportListLink="/ReportAll"
                    >
                        {summaryCards.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.25,
                                    mb: 2,
                                    px: { xs: 0.5, md: 1 },
                                }}
                            >
                                {summaryCards.map((card, index) => (
                                    <Box
                                        key={card.label}
                                        sx={{
                                            minWidth: 120,
                                            borderRadius: '12px',
                                            px: 1.75,
                                            py: 1.1,
                                            color: '#fff',
                                            background: index % 2 === 0
                                                ? 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #60a5fa 100%)'
                                                : 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #2dd4bf 100%)',
                                            boxShadow: '0 8px 20px rgba(37, 99, 235, 0.18)',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9 }}>
                                            {card.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
                                            {card.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <ReactTableWrapper
                            columns={REPORT_COLUMNS}
                            data={filteredRecords}
                            exportFilename={exportFileBase}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            enableTableSearch={false}
                            compactRows
                            layoutFullWidth
                            defaultPageSize={10}
                            left_pinned={['marketing_name']}
                        />
                    </ListPageLayout>
                </div>
            </div>
        </div>
    );
}

export default MarketingPersonReport;
